import { useEffect, useRef, useState } from "react";
import { navigate, routes, useCurrentUrl } from "../lib/urls";

type Args = {
  agentStepName: string | undefined;
  agentFilePath: string | undefined;
  latestExecutionId: string | undefined;
  isResponding: boolean;
  autoFollow: boolean;
  branchId: string | undefined;
};

export function useFollow3b({
  agentStepName,
  agentFilePath,
  latestExecutionId,
  isResponding,
  autoFollow,
  branchId,
}: Args) {
  const { step, file } = useCurrentUrl(routes.space.workflow);
  const [isFollowing, setIsFollowing] = useState(false);

  const intendedStepRef = useRef<string | null>(null);
  const intendedFileRef = useRef<string | null>(null);
  const lastSeenLatestExecutionRef = useRef<string | undefined>(
    latestExecutionId
  );

  // If the URL drifts from what we last navigated to, the user moved manually
  // — drop out of follow.
  useEffect(() => {
    if (!isFollowing) {
      intendedStepRef.current = null;
      intendedFileRef.current = null;
      return;
    }
    if (intendedStepRef.current === null) {
      intendedStepRef.current = step ?? null;
      intendedFileRef.current = file ?? null;
      return;
    }
    if (step !== intendedStepRef.current) {
      setIsFollowing(false);
      return;
    }
    if (intendedFileRef.current !== null && file !== intendedFileRef.current) {
      setIsFollowing(false);
    }
  }, [isFollowing, step, file]);

  useEffect(() => {
    // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
    if (autoFollow && isResponding) setIsFollowing(true);
  }, [autoFollow, isResponding, branchId]);

  useEffect(() => {
    if (!isFollowing) return;
    if (!isResponding || !agentStepName) return;
    const stepChanged = agentStepName !== step;
    const fileChanged = agentFilePath != null && agentFilePath !== file;
    if (!stepChanged && !fileChanged) return;
    intendedStepRef.current = agentStepName;
    // On a step change without a known file, clear `file` so the new step
    // opens its default file instead of stranding the user on a stale path.
    const targetFile = agentFilePath ?? (stepChanged ? "" : undefined);
    if (targetFile !== undefined) {
      intendedFileRef.current = targetFile === "" ? null : targetFile;
    }
    navigate(routes.space.workflow, {
      step: agentStepName,
      ...(targetFile !== undefined && { file: targetFile }),
    });
  }, [isFollowing, isResponding, agentStepName, agentFilePath, step, file]);

  // When the agent runs the followed step, jump to the new execution by
  // clearing any sticky `execution` URL param the user had set on an older one.
  useEffect(() => {
    if (!isFollowing) {
      lastSeenLatestExecutionRef.current = latestExecutionId;
      return;
    }
    if (
      latestExecutionId &&
      latestExecutionId !== lastSeenLatestExecutionRef.current
    ) {
      lastSeenLatestExecutionRef.current = latestExecutionId;
      navigate(routes.space.workflow, { execution: "" });
    }
  }, [isFollowing, latestExecutionId]);

  return {
    isFollowing,
    setIsFollowing,
  };
}
